import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { SliteLogo } from '@slite/components'
import {
  AppleIcon,
  GlobeIcon,
  type Icon,
  MobileIcon,
  PlugIcon,
  WindowsIcon,
} from '@slite/icons'

import { Button } from '../../components/Button/Button'
import { Section } from '../../components/Section'
import { SlitePage } from '../../components/SlitePage'
import { WebsiteText } from '../../components/WebsiteText'
import { useRoutes } from '../../routes'

type PlatformRowProps = {
  icon: Icon
  label: React.ReactNode
  href: string
  target?: '_blank'
  buttonLabel: React.ReactNode
  isLast?: boolean
}

function PlatformRow({
  icon: PlatformIcon,
  label,
  href,
  target,
  buttonLabel,
  isLast,
}: PlatformRowProps) {
  return (
    <div
      className={
        isLast
          ? 'flexbox flexbox-align-center flexbox-space-between padding-vertical-16'
          : 'flexbox flexbox-align-center flexbox-space-between padding-vertical-16 border-bottom-small border-alpha-shade-40'
      }
    >
      <div className="flexbox flexbox-align-center flexbox-column-gap-12">
        <PlatformIcon size={18} className="shade-05" />
        <span className="font-size-6 font-weight-semibold shade-05">
          {label}
        </span>
      </div>
      <a
        href={href}
        target={target}
        rel={target === '_blank' ? 'noreferrer' : undefined}
        className="font-size-9 font-family-system font-weight-semibold padding-vertical-8 padding-horizontal-16 radius-largest transition shade-05 background-moon-dust background-moon-dust-darker-on-hover"
        style={{ textDecoration: 'none' }}
      >
        {buttonLabel}
      </a>
    </div>
  )
}

type PlatformSectionProps = {
  title: React.ReactNode
  description: React.ReactNode
  children: React.ReactNode
}

function PlatformSection({
  title,
  description,
  children,
}: PlatformSectionProps) {
  return (
    <div
      className="grid-12 grid-gap-40 border-top-small border-alpha-shade-45"
      style={{ marginBottom: 80 }}
    >
      <div className="grid-span-4 grid-span-12-medium grid-span-12-small padding-top-24">
        <WebsiteText
          variant="smallTitle"
          htmlTag="h2"
          className="margin-bottom-8"
        >
          {title}
        </WebsiteText>
        <WebsiteText variant="body" className="shade-20 padding-right-24">
          {description}
        </WebsiteText>
      </div>
      <div className="grid-span-8 grid-span-12-medium grid-span-12-small">
        {children}
      </div>
    </div>
  )
}

// Defaults to false so SSR + first client render both produce the macOS CTA.
// After mount we sniff navigator.userAgent and flip to Windows when applicable.
function useIsWindows() {
  const [isWindows, setIsWindows] = useState(false)
  useEffect(() => {
    if (typeof navigator === 'undefined') return
    setIsWindows(/windows/i.test(navigator.userAgent))
  }, [])
  return isWindows
}

export default function DownloadPage() {
  const routes = useRoutes()
  const intl = useIntl()
  const isWindows = useIsWindows()

  const metaTitle = intl.formatMessage({
    id: 'download.page.title',
    defaultMessage: 'Download Slite for your device',
  })
  const metaDescription = intl.formatMessage({
    id: 'download.page.description',
    defaultMessage:
      'Download Slite for your device to keep a quicker access to your team’s work throughout the day.',
  })

  return (
    <SlitePage
      title={metaTitle}
      description={metaDescription}
      ogImage="https://cdn.sanity.io/images/dnsgqii5/production/a95c229d40b4ffe511d87f99469e098aa2964b9e-1200x630.jpg"
      background="moon-dust-lighter"
      canonicalUrl={routes.download()}
    >
      <Section background="moon-dust-lighter" className="text-align-center">
        <div className="grid-12">
          <div className="grid-span-8-large grid-offset-2-large grid-span-12 flexbox flexbox-column flexbox-align-center">
            <div
              className="flexbox flexbox-align-center flexbox-column-gap-12 margin-bottom-32"
              style={{
                maskImage:
                  'linear-gradient(to right, transparent 0%, black 30%, black 70%, transparent 100%)',
                WebkitMaskImage:
                  'linear-gradient(to right, transparent 0%, black 30%, black 70%, transparent 100%)',
              }}
            >
              <div
                className="background-transparent border-small border-alpha-shade-45 flexbox flexbox-centered"
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: 18,
                  background: 'var(--shade-100)',
                }}
                aria-hidden
              />
              <div
                className="background-transparent border-small border-alpha-shade-45 flexbox flexbox-centered"
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: 18,
                  background: 'var(--shade-100)',
                }}
                aria-hidden
              />
              <div
                className="flexbox flexbox-centered"
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: 18,
                  border: '2px solid transparent',
                  background: 'var(--shade-00)',
                }}
              >
                <SliteLogo
                  variant="symbol"
                  style={{ width: 70 }}
                  className="shade-100"
                />
              </div>
              <div
                className="background-transparent border-small border-alpha-shade-45 flexbox flexbox-centered"
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: 18,
                  background: 'var(--shade-100)',
                }}
                aria-hidden
              />
              <div
                className="background-transparent border-small border-alpha-shade-45 flexbox flexbox-centered"
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: 18,
                  background: 'var(--shade-100)',
                }}
                aria-hidden
              />
            </div>

            <WebsiteText
              variant="display"
              htmlTag="h1"
              className="margin-bottom-16"
            >
              <FormattedMessage
                id="download.page.hero.title"
                defaultMessage="Download Slite"
              />
            </WebsiteText>
            <WebsiteText
              variant="body"
              className="font-weight-semibold margin-bottom-32 shade-20"
              style={{ maxWidth: 500 }}
            >
              <FormattedMessage
                id="download.page.hero.subtitle"
                defaultMessage="Available on macOS, Windows, iOS, Android, and as a Chrome extension."
              />
            </WebsiteText>
            <Button
              variant="primary"
              icon={isWindows ? WindowsIcon : AppleIcon}
              href={
                isWindows
                  ? routes.external.windowsDownload.url
                  : routes.external.macDownload.url
              }
            >
              {isWindows ? (
                <FormattedMessage
                  id="download.page.hero.windowsCta"
                  defaultMessage="Download for Windows"
                />
              ) : (
                <FormattedMessage
                  id="download.page.hero.macCta"
                  defaultMessage="Download for macOS"
                />
              )}
            </Button>
          </div>
        </div>
      </Section>

      <Section background="moon-dust-lighter" containerTopPadding={0}>
        <div className="grid-12">
          <div className="grid-span-10-large grid-offset-1-large grid-span-12">
            <PlatformSection
              title={
                <FormattedMessage
                  id="download.page.desktop.title"
                  defaultMessage="Slite Desktop."
                />
              }
              description={
                <FormattedMessage
                  id="download.page.desktop.description"
                  defaultMessage="A focused workspace for your team's docs, on every screen you work from."
                />
              }
            >
              <PlatformRow
                icon={AppleIcon}
                label={
                  <FormattedMessage
                    id="download.page.platform.macOS"
                    defaultMessage="macOS"
                  />
                }
                href={routes.external.macDownload.url}
                buttonLabel={
                  <FormattedMessage
                    id="download.page.button.download"
                    defaultMessage="Download"
                  />
                }
              />
              <PlatformRow
                icon={WindowsIcon}
                label={
                  <FormattedMessage
                    id="download.page.platform.windows"
                    defaultMessage="Windows"
                  />
                }
                href={routes.external.windowsDownload.url}
                buttonLabel={
                  <FormattedMessage
                    id="download.page.button.download"
                    defaultMessage="Download"
                  />
                }
              />
              <PlatformRow
                icon={GlobeIcon}
                label={
                  <FormattedMessage
                    id="download.page.platform.web"
                    defaultMessage="Web app"
                  />
                }
                href={routes.auth.signin()}
                buttonLabel={
                  <FormattedMessage
                    id="download.page.button.open"
                    defaultMessage="Open"
                  />
                }
                isLast
              />
            </PlatformSection>

            <PlatformSection
              title={
                <FormattedMessage
                  id="download.page.mobile.title"
                  defaultMessage="Slite Mobile."
                />
              }
              description={
                <FormattedMessage
                  id="download.page.mobile.description"
                  defaultMessage="Read, comment, and stay in the loop wherever you are."
                />
              }
            >
              <PlatformRow
                icon={AppleIcon}
                label={
                  <FormattedMessage
                    id="download.page.platform.ios"
                    defaultMessage="iOS"
                  />
                }
                href={routes.external.appleAppStore.url}
                target="_blank"
                buttonLabel={
                  <FormattedMessage
                    id="download.page.button.openStore"
                    defaultMessage="Open store"
                  />
                }
              />
              <PlatformRow
                icon={MobileIcon}
                label={
                  <FormattedMessage
                    id="download.page.platform.android"
                    defaultMessage="Android"
                  />
                }
                href={routes.external.androidAppStore.url}
                target="_blank"
                buttonLabel={
                  <FormattedMessage
                    id="download.page.button.openStore"
                    defaultMessage="Open store"
                  />
                }
                isLast
              />
            </PlatformSection>

            <PlatformSection
              title={
                <FormattedMessage
                  id="download.page.agentExtension.title"
                  defaultMessage="Slite Agent."
                />
              }
              description={
                <FormattedMessage
                  id="download.page.agentExtension.description"
                  defaultMessage="AI search across all your tools, from any tab in your browser."
                />
              }
            >
              <PlatformRow
                icon={PlugIcon}
                label={
                  <FormattedMessage
                    id="download.page.platform.chrome"
                    defaultMessage="Chrome"
                  />
                }
                href={routes.external.agentChromeExtension.url}
                target="_blank"
                buttonLabel={
                  <FormattedMessage
                    id="download.page.button.install"
                    defaultMessage="Install"
                  />
                }
                isLast
              />
            </PlatformSection>

            <PlatformSection
              title={
                <FormattedMessage
                  id="download.page.extension.title"
                  defaultMessage="Draft by Slite."
                />
              }
              description={
                <FormattedMessage
                  id="download.page.extension.description"
                  defaultMessage="Quick notes and instant answers from any tab in your browser."
                />
              }
            >
              <PlatformRow
                icon={PlugIcon}
                label={
                  <FormattedMessage
                    id="download.page.platform.chrome"
                    defaultMessage="Chrome"
                  />
                }
                href={routes.external.draftChromeExtension.url}
                target="_blank"
                buttonLabel={
                  <FormattedMessage
                    id="download.page.button.install"
                    defaultMessage="Install"
                  />
                }
                isLast
              />
            </PlatformSection>
          </div>
        </div>
      </Section>
    </SlitePage>
  )
}
